/**
 * Design tokens for the Clinvea clinical shell — the dark-navy sidebar, the top bar, and the
 * three-column consultation workspace.
 *
 * These mirror the approved HTML design exactly (its `:root` custom properties). They live here
 * rather than in `clinicTheme.ts` because that theme is MUI's palette for the whole product; these
 * are the specific surface/line/tint values the clinical shell is drawn with, and several have no
 * MUI palette equivalent (`nav`, `navHover`, `lineSoft`, the `*Tint` pairs).
 *
 * Light and dark are given as separate maps and selected by `design(mode)`. The source design is
 * light-only; the dark values are derived to keep the same contrast relationships, so the app's
 * existing dark mode does not break on screens that adopt these tokens.
 */

export interface ClinveaDesign {
  bg: string;
  surface: string;
  nav: string;
  navHover: string;
  navText: string;
  navLabel: string;
  ink: string;
  muted: string;
  faint: string;
  line: string;
  lineSoft: string;
  brand: string;
  brandDeep: string;
  tint: string;
  green: string;
  greenTint: string;
  amber: string;
  amberTint: string;
  red: string;
  redTint: string;
  violet: string;
  violetTint: string;
  radius: number;
}

const light: ClinveaDesign = {
  bg:        '#F4F6F8',
  surface:   '#FFFFFF',
  nav:       '#0F1B2D',
  navHover:  '#1B2A41',
  navText:   '#C3CCDA',
  navLabel:  '#61708A',
  ink:       '#111827',
  muted:     '#5B6675',
  faint:     '#8A94A3',
  line:      '#E3E7EC',
  lineSoft:  '#EDF0F3',
  brand:     '#1D6AE5',
  brandDeep: '#1557C2',
  tint:      '#EAF1FE',
  green:     '#0E8A5F',
  greenTint: '#E4F5EE',
  amber:     '#B45309',
  amberTint: '#FCF0DF',
  red:       '#C8372D',
  redTint:   '#FCEBEA',
  violet:    '#6D48C7',
  violetTint:'#F0EBFB',
  radius:    10,
};

/**
 * The navy sidebar is already near-black, so it is kept as-is in dark mode — inverting it would
 * make the one fixed landmark on screen move. Surfaces and lines lift off the background instead.
 */
const dark: ClinveaDesign = {
  ...light,
  bg:        '#0B1017',
  surface:   '#141A22',
  nav:       '#0A121E',
  navHover:  '#16233A',
  ink:       '#E7EBF0',
  muted:     '#9AA5B4',
  faint:     '#6B7683',
  line:      '#242D39',
  lineSoft:  '#1C242E',
  brand:     '#4C8DF6',
  brandDeep: '#1D6AE5',
  tint:      'rgba(76,141,246,0.14)',
  greenTint: 'rgba(14,138,95,0.18)',
  amberTint: 'rgba(180,83,9,0.18)',
  redTint:   'rgba(200,55,45,0.18)',
  violetTint:'rgba(109,72,199,0.18)',
};

export const design = (mode: 'light' | 'dark' | string | undefined): ClinveaDesign =>
  mode === 'dark' ? dark : light;

/** Card surface used by every panel in the shell. */
export const cardSx = (d: ClinveaDesign) => ({
  bgcolor: d.surface,
  border: '1px solid',
  borderColor: d.line,
  borderRadius: `${d.radius}px`,
});

/** Header row inside a card: title on the left, an optional action on the right. */
export const cardHeadSx = (d: ClinveaDesign) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.75,
  py: 1.5,
  borderBottom: '1px solid',
  borderColor: d.lineSoft,
});

/**
 * The status/severity badge used for queue rows, patient status and lab values. Returns the
 * background/foreground pair for a tone so callers never hand-pick a tint that doesn't match its
 * text colour.
 */
export type Tone = 'brand' | 'green' | 'amber' | 'red' | 'violet' | 'neutral';

export const toneSx = (d: ClinveaDesign, tone: Tone) => {
  switch (tone) {
    case 'green':  return { bgcolor: d.greenTint,  color: d.green };
    case 'amber':  return { bgcolor: d.amberTint,  color: d.amber };
    case 'red':    return { bgcolor: d.redTint,    color: d.red };
    case 'violet': return { bgcolor: d.violetTint, color: d.violet };
    case 'brand':  return { bgcolor: d.tint,       color: d.brandDeep };
    default:       return { bgcolor: d.lineSoft,   color: d.muted };
  }
};

export const badgeSx = (d: ClinveaDesign, tone: Tone) => ({
  display: 'inline-block',
  fontSize: '0.66rem',
  fontWeight: 600,
  px: 0.9,
  py: 0.25,
  borderRadius: '5px',
  whiteSpace: 'nowrap',
  lineHeight: 1.45,
  ...toneSx(d, tone),
});

/** Uppercase micro-label above a value (vitals strip, stat tiles). */
export const microLabelSx = (d: ClinveaDesign) => ({
  fontSize: '0.655rem',
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase' as const,
  color: d.faint,
});
